import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Select, Card, Typography, Space, Alert } from 'antd';
import { PhoneOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { SendOtpRequest, LoginRequest } from '../types';

// eslint-disable-next-line no-empty-pattern
const { } = Typography;
const { Option } = Select;

const LoginPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { isRTL } = useLocale();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('09132655299');
  const [countryCode, setCountryCode] = useState('+98');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Timer effect for resend countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResendOtp = async () => {
    if (resendTimer > 0) return; // Don't allow resend if timer is active
    
    setLoading(true);
    setError('');
    try {
      const cleanPhoneNumber = phoneNumber.startsWith('0') ? phoneNumber.substring(1) : phoneNumber;
      const data: SendOtpRequest = { 
        phoneNumber: cleanPhoneNumber, 
        countryCode 
      };
      
      const result = await apiService.sendOtp(data);
      
      if (result.otp) {
        console.log('⭐ کد OTP جدید شما:', result.otp);
        alert('✅ کد تایید جدید ارسال شد!\nکد شما: ' + result.otp + '\n\n(این فقط در حالت توسعه نمایش داده می‌شود)');
      }
      
      setResendTimer(60); // Restart 60 second countdown
    } catch (err: any) {
      console.error('Resend OTP Error:', err);
      setError('ارسال مجدد کد تایید ناموفق بود: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setError(t('auth.invalidPhone'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      // حذف صفر ابتدای شماره موبایل اگر وجود دارد
      const cleanPhoneNumber = phoneNumber.startsWith('0') ? phoneNumber.substring(1) : phoneNumber;
      const data: SendOtpRequest = { 
        phoneNumber: cleanPhoneNumber, 
        countryCode 
      };
      
      console.log('Sending OTP with data:', data);
      const result = await apiService.sendOtp(data);
      console.log('OTP Response:', result);
      
      // نمایش OTP در کنسول و در صفحه (فقط برای development)
      if (result.otp) {
        console.log('⭐ کد OTP شما:', result.otp);
        alert('✅ کد تایید ارسال شد!\nکد شما: ' + result.otp + '\n\n(این فقط در حالت توسعه نمایش داده می‌شود)');
      }
      
      setOtpSent(true);
      setResendTimer(60); // Start 60 second countdown
    } catch (err: any) {
      console.error('OTP Error:', err);
      setError('ارسال کد تایید ناموفق بود: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!otp || otp.length !== 6) {
      setError(t('auth.invalidOtp'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      // حذف صفر ابتدای شماره موبایل اگر وجود دارد
      const cleanPhoneNumber = phoneNumber.startsWith('0') ? phoneNumber.substring(1) : phoneNumber;
      const data: LoginRequest = { 
        phoneNumber: cleanPhoneNumber, 
        otp 
      };
      
      console.log('Login request:', data);
      const result = await apiService.login(data);
      console.log('Login Response:', result);
      
      login(result.accessToken, {
        phoneNumber: cleanPhoneNumber,
        role: result.user?.role,
        firstName: result.user?.firstName,
        lastName: result.user?.lastName,
        id: result.user?.id,
      });
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Login Error:', err);
      setError('ورود ناموفق بود: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      {/* Language Switcher */}
      <div style={{ 
        position: 'absolute',
        top: '20px',
        ...(isRTL ? { right: '20px', left: 'auto' } : { left: '20px', right: 'auto' }),
        zIndex: 1000
      }}>
        <LanguageSwitcher type="button" />
      </div>
      
      {/* Logo outside the box */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '30px',
        zIndex: 10
      }}>
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '25px',
          background: 'rgba(255,255,255,0.15)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(255,255,255,0.2)',
          margin: '0 auto',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 12px 40px rgba(255,255,255,0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(255,255,255,0.2)';
        }}
        >
          <img 
            src="/logo.png" 
            alt="Travel Super App Logo" 
            style={{ 
              width: '70px', 
              height: '70px', 
              borderRadius: '15px',
              filter: 'brightness(0) invert(1)',
              objectFit: 'contain'
            }} 
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallbackDiv = document.createElement('div');
              fallbackDiv.style.cssText = `
                width: 70px;
                height: 70px;
                background: rgba(255,255,255,0.2);
                border-radius: 15px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 32px;
                color: white;
                font-weight: bold;
                font-family: 'Vazirmatn', sans-serif;
              `;
              fallbackDiv.textContent = 'P';
              e.currentTarget.parentNode?.appendChild(fallbackDiv);
            }}
          />
        </div>
      </div>

      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 400,
          borderRadius: '20px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>

          <Form layout="vertical">
            <Form.Item required>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                background: 'white',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                height: '56px'
              }}>
                <Input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="09132655299"
                  style={{ 
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: '16px',
                    fontFamily: 'Vazirmatn, sans-serif',
                    background: 'transparent',
                    padding: '12px 16px',
                    height: '52px',
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                  disabled={otpSent}
                />
                <div style={{ 
                  width: '1px', 
                  height: '32px', 
                  background: '#e2e8f0',
                  margin: '0 8px'
                }} />
                <Select 
                  value={countryCode} 
                  onChange={setCountryCode}
                  style={{ 
                    width: '120px',
                    border: 'none',
                    background: '#f8fafc',
                    height: '52px',
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                  dropdownStyle={{
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                  disabled={otpSent}
                  suffixIcon={<PhoneOutlined style={{ color: '#64748b' }} />}
                >
                  <Option value="+98">🇮🇷 +98</Option>
                  <Option value="+1">🇺🇸 +1</Option>
                  <Option value="+44">🇬🇧 +44</Option>
                </Select>
              </div>
            </Form.Item>

            {!otpSent ? (
              <Button 
                type="primary" 
                size="large"
                onClick={handleSendOtp}
                loading={loading}
                style={{ 
                  width: '100%',
                  height: '56px',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: 'Vazirmatn, sans-serif',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                icon={<PhoneOutlined />}
              >
                {t('auth.sendOtp')}
              </Button>
            ) : (
              <>
                <Form.Item required>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    background: 'white',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    height: '56px'
                  }}>
                    <Input
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="123456"
                      maxLength={6}
                      style={{ 
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: '16px',
                        fontFamily: 'Vazirmatn, sans-serif',
                        background: 'transparent',
                        padding: '12px 16px',
                        height: '52px',
                        direction: 'ltr',
                        textAlign: 'center'
                      }}
                    />
                    <SafetyCertificateOutlined style={{ 
                      marginRight: '16px',
                      color: '#64748b',
                      fontSize: '18px'
                    }} />
                  </div>
                </Form.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    size="large"
                    onClick={handleLogin}
                    loading={loading}
                    style={{ 
                      width: '100%',
                      height: '56px',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '600',
                      fontFamily: 'Vazirmatn, sans-serif',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {t('auth.login')}
                  </Button>
                  <Button 
                    onClick={handleResendOtp}
                    disabled={resendTimer > 0 || loading}
                    loading={loading}
                    style={{ 
                      width: '100%',
                      height: '48px',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontFamily: 'Vazirmatn, sans-serif',
                      border: resendTimer > 0 ? '2px solid #d1d5db' : '2px solid #e2e8f0',
                      background: resendTimer > 0 ? '#f9fafb' : 'white',
                      color: resendTimer > 0 ? '#9ca3af' : '#64748b'
                    }}
                  >
                    {resendTimer > 0 ? t('auth.resendTimer', { seconds: resendTimer }) : t('auth.resendOtp')}
                  </Button>
                </Space>
              </>
            )}
          </Form>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
            />
          )}
        </Space>
      </Card>
    </div>
  );
};

export default LoginPage;
